import {
  ButtonRow,
  FlexLoadingBlock,
  Page,
  PageHeader,
  useModal,
  DetailsTree,
  DetailsTreeItemValue,
  useTreeViewApiRef,
  ButtonLink,
  useSearchParamsForm,
  InputField,
  USER_ROLE_ICON_MAP,
} from "@synota-io/synota-shared-ui";
import {
  Alert,
  AlertTitle,
  Button,
  IconButton,
  Stack,
  Box,
  CircularProgress,
  Typography,
  Chip,
} from "@mui/material";
import { PriceCheckOutlined, OpenInNew, Close, SupervisorAccount } from "@mui/icons-material";
import { useMemo, useEffect, useState } from "react";
import { useNodeUrls } from "../../../api/hooks/useNodeUrls";
import { INodeUser, UseNodeUsersResponse } from "../../../api/hooks/useNodeUsers";
import { NodesBreadcrumbs } from "../components/NodesBreadcrumbs";
import { PennyTestFormModal } from "../modals/PennyTestFormModal";
import { NodeUserDetailsModal } from "../modals/NodeUserDetailsModal";
import { getUrlTitle } from "../utils";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { NODES_PATH, NODE_LINK_USERS_PATH, NODE_USER_DETAILS_PATH } from "../../../paths";
import { generatePath } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useHttp } from "../../../api/http/useHttp";
import { useAuth } from "../../../api/auth/useAuth";
import groupBy from "lodash/groupBy";
import { AxiosResponse } from "axios";
import { LinkUsersModalForm } from "../modals/LinkUsersModalForm";

export const NodeUrlsPage = () => {
  const { nodeUrls, isLoading: isNodesLoading } = useNodeUrls();
  const pennyTestModal = useModal();
  const userDetailsModal = useModal();
  const linkUsersModal = useModal();
  const navigate = useNavigate();
  const { search } = useLocation();
  const apiRef = useTreeViewApiRef();
  const queryClient = useQueryClient();
  const { client } = useHttp();
  const { isAdmin } = useAuth();

  const userDetailsMatch = useMatch(NODE_USER_DETAILS_PATH);
  const linkUsersMatch = useMatch(NODE_LINK_USERS_PATH);

  const nodeUuid = userDetailsMatch?.params.nodeUuid;

  const filterForm = useSearchParamsForm(
    {
      defaultValues: {
        url_search: "",
      },
    },
    {
      url_search: true,
    },
  );

  const { url_search } = filterForm.watch();

  const filteredNodes = useMemo(() => {
    if (!url_search) return nodeUrls ?? [];
    return (nodeUrls ?? []).filter(({ dashboard_url }) =>
      dashboard_url.toLowerCase().includes(url_search.toLowerCase()),
    );
  }, [nodeUrls, url_search]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    if (nodeUuid) {
      return [`node_${nodeUuid}`];
    }
    return [];
  });

  const [childrenMap, setChildrenMap] = useState<Record<string, DetailsTreeItemValue[]>>({});

  const treeItems: DetailsTreeItemValue[] = useMemo(() => {
    if (!filteredNodes.length) {
      return [
        {
          id: "root_empty",
          label: "",
          render: () => (
            <Alert severity="info">
              <AlertTitle>
                {url_search
                  ? "No nodes match the search criteria."
                  : "There are no nodes available."}
              </AlertTitle>
            </Alert>
          ),
        },
      ];
    }

    return filteredNodes.map(({ dashboard_url, uuid }) => {
      const nodeId = `node_${uuid}`;
      const children =
        childrenMap[nodeId] ??
        (expandedItems.includes(nodeId) ? [getLoadingItem(nodeId)] : [getPlaceholderItem(nodeId)]);
      return {
        id: nodeId,
        label: getUrlTitle(dashboard_url),
        action: (
          <ButtonRow>
            <Button
              size="small"
              color="primary"
              variant="text"
              startIcon={<SupervisorAccount />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  generatePath(NODE_LINK_USERS_PATH, {
                    linkNodeUuid: uuid,
                  }),
                );
              }}
            >
              <Box component="span" display={{ xs: "none", sm: "inline" }}>
                Link Users
              </Box>
            </Button>
            <ButtonLink
              onClick={(e) => {
                e.stopPropagation();
              }}
              startIcon={<OpenInNew />}
              to={dashboard_url}
              target="_blank"
              size="small"
              color="primary"
              variant="text"
            >
              <Box component="span" display={{ xs: "none", sm: "inline" }}>
                Open Dashboard
              </Box>
            </ButtonLink>
          </ButtonRow>
        ),
        children,
      } satisfies DetailsTreeItemValue;
    });
  }, [filteredNodes, url_search, childrenMap, expandedItems, navigate]);

  useEffect(() => {
    if (!isAdmin) return;

    filteredNodes.forEach(({ uuid }) => {
      const nodeId = `node_${uuid}`;
      if (expandedItems.includes(nodeId) && !childrenMap[nodeId]) {
        setChildrenMap((prev) => ({
          ...prev,
          [nodeId]: [getLoadingItem(nodeId)],
        }));

        (async () => {
          try {
            const input = { server_uuid: uuid };
            const queryKey = ["node-list-users", input];
            const queryFn = async () => await client.post("/synota/list-users", input);
            const data: AxiosResponse<UseNodeUsersResponse> = await queryClient.fetchQuery({
              queryKey,
              queryFn,
            });
            queryClient.setQueryData(queryKey, data);

            const nodeUsers = (data?.data.users ?? []).sort((a, b) => a.role.localeCompare(b.role));

            let items: DetailsTreeItemValue[] = [];

            if (!nodeUsers.length) {
              items = [getNoUsersItem(nodeId)];
            } else {
              const grouped = groupBy(nodeUsers, ({ user_group_id }) => user_group_id ?? null);
              const groupedUsers = Object.entries(grouped).filter(([id]) => id !== "null");
              const ungroupedUsers = grouped.null ?? [];

              groupedUsers.forEach(([groupId, users]) => {
                const groupName = users[0].user_group_name || "Unnamed Group";
                items.push(getGroupItem(nodeId, groupId, groupName, users, uuid, navigate));
              });

              ungroupedUsers.forEach((user) => {
                items.push(getUserItem(nodeId, user, uuid, navigate));
              });

              if (items.length === 0) {
                items = [getNoUsersItem(nodeId)];
              }

              if (groupedUsers.length === 1 && ungroupedUsers.length === 0) {
                const [groupId] = groupedUsers[0];
                const groupItemId = `${nodeId}_group_${groupId}`;
                setExpandedItems((prev) => {
                  if (prev.includes(groupItemId)) return prev;
                  return [...prev, groupItemId];
                });
              }
            }

            setChildrenMap((prev) => ({ ...prev, [nodeId]: items }));
          } catch {
            setChildrenMap((prev) => ({ ...prev, [nodeId]: [getErrorItem(nodeId)] }));
          }
        })();
      }
    });
  }, [expandedItems, filteredNodes, navigate, queryClient, client, isAdmin, childrenMap]);

  useEffect(() => {
    if (userDetailsMatch && !userDetailsModal.open && !isNodesLoading) {
      userDetailsModal.onOpen();
    }
    if (linkUsersMatch && !linkUsersModal.open && !isNodesLoading) {
      linkUsersModal.onOpen();
    }
  }, [userDetailsMatch, linkUsersMatch, isNodesLoading, userDetailsModal, linkUsersModal]);

  if (isNodesLoading) {
    return <FlexLoadingBlock>Fetching nodes...</FlexLoadingBlock>;
  }

  return (
    <Page>
      <Stack gap={6}>
        <PageHeader
          title={<NodesBreadcrumbs />}
          actions={
            <ButtonRow>
              <InputField
                label="Filter by URL"
                autoComplete="off"
                variant="filled"
                control={filterForm.control}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton
                        disabled={!url_search}
                        onClick={() => filterForm.reset({ url_search: "" })}
                        size="small"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    ),
                  },
                }}
                name="url_search"
              />
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<PriceCheckOutlined />}
                title="Create Penny Test Contract"
                onClick={() => pennyTestModal.onOpen()}
              >
                Penny Test
              </Button>
            </ButtonRow>
          }
        />
        <DetailsTree
          key={treeItems.length}
          multiSelect
          apiRef={apiRef}
          items={treeItems}
          expandedItems={expandedItems}
          onExpandedItemsChange={(_event, items) => setExpandedItems(items)}
          selectedItems={selectedItems}
          onSelectedItemsChange={(_event, items) => setSelectedItems(items)}
        />
      </Stack>
      <PennyTestFormModal key={String(pennyTestModal.open)} {...pennyTestModal} />
      <NodeUserDetailsModal
        {...userDetailsModal}
        onClose={() => {
          userDetailsModal.onClose();
          navigate(
            { pathname: NODES_PATH, search },
            { preventScrollReset: true, relative: "path", replace: true },
          );
        }}
      />
      <LinkUsersModalForm
        key={String(linkUsersModal.open)}
        {...linkUsersModal}
        onSuccess={({ node }) => {
          if (node.value) {
            const uuid = node.value;
            const nodeId = `node_${uuid}`;
            queryClient.invalidateQueries({ queryKey: ["node-list-users", { server_uuid: uuid }] });

            setSelectedItems([nodeId]);
            setExpandedItems((current) => [...current, nodeId]);

            setChildrenMap((prev) => {
              const newMap = { ...prev };
              delete newMap[nodeId];
              return newMap;
            });
          }
        }}
        onClose={() => {
          linkUsersModal.onClose();
          navigate(
            { pathname: NODES_PATH, search },
            { preventScrollReset: true, relative: "path", replace: true },
          );
        }}
      />
    </Page>
  );
};

const getLoadingItem = (nodeId: string): DetailsTreeItemValue => ({
  id: `${nodeId}_loading`,
  label: "",
  render: () => (
    <Stack direction="row" gap={4} alignItems="center">
      <CircularProgress size="1em" />
      <Typography variant="caption" color="text.secondary">
        Loading users...
      </Typography>
    </Stack>
  ),
});

const getErrorItem = (nodeId: string): DetailsTreeItemValue => ({
  id: `${nodeId}_error`,
  label: "",
  render: () => (
    <Alert severity="error">
      <AlertTitle>Failed to load users</AlertTitle>
    </Alert>
  ),
});

const getNoUsersItem = (nodeId: string): DetailsTreeItemValue => ({
  id: `${nodeId}_no_users`,
  label: "",
  render: () => (
    <Alert severity="warning">
      <AlertTitle>There are no users on this node.</AlertTitle>
    </Alert>
  ),
});

const getPlaceholderItem = (nodeId: string): DetailsTreeItemValue => ({
  id: `${nodeId}_placeholder`,
  label: "",
  render: () => null,
});

const getUserItem = (
  parentId: string,
  user: INodeUser,
  uuid: string,
  navigate: ReturnType<typeof useNavigate>,
): DetailsTreeItemValue => {
  const RoleIcon = USER_ROLE_ICON_MAP[user.role];
  return {
    id: `${parentId}_user_${user.uuid}`,
    label: "",
    render: () => (
      <Typography display="flex" flexDirection="column" variant="subtitle1" fontSize="small">
        <Box color="text.secondary">{user.public_name}</Box>
        <div>{user.email}</div>
      </Typography>
    ),
    onDoubleClick: () => {
      navigate(
        generatePath(NODE_USER_DETAILS_PATH, {
          nodeUuid: uuid,
          userUuid: user.uuid,
        }),
        { replace: true },
      );
    },
    action: (
      <Stack direction="row" color="text.disabled">
        <Chip size="small" label={user.role} icon={<RoleIcon />} />
      </Stack>
    ),
  };
};

const getGroupItem = (
  nodeId: string,
  groupId: string,
  groupName: string,
  users: INodeUser[],
  uuid: string,
  navigate: ReturnType<typeof useNavigate>,
): DetailsTreeItemValue => {
  const groupItemId = `${nodeId}_group_${groupId}`;
  const admin = users.find((u) => u.role === "admin");
  return {
    id: groupItemId,
    label: groupName,
    action: admin ? (
      <Stack direction="row" gap={2}>
        <SupervisorAccount fontSize="small" />
        <Typography variant="subtitle1" color="text.secondary" fontSize="small">
          {admin.email}
        </Typography>
      </Stack>
    ) : null,
    children: users.map((user) => getUserItem(groupItemId, user, uuid, navigate)),
  };
};
